import { isPerUnitPricing, isRevShareProductVariant, isTieredSubscriptionPricing } from '@vy/services';
import type { CatalogProducts, ERPIntegrationEntityMetadata, Pricing } from '@vy/types';
import { type UsageProduct, type UsageProductVariant } from '@vy/types';
import { formatNumber, isNotEmptyArray, withNewOptionalValue } from '@vy/utils';
import { formatDate } from '../../../../../utils/date';
import { getErpProduct } from '../../ContractFixedFees/contract-fixed-fees-utils/get-contract-fixed-fees-rows';
import { resolveAnrokFields } from '../../shared/resolve-anrok-fields';
import type { UsageProductsColumns } from '../contract-usage-products-types/usage-products-columns';
import { getPricingTypeLabel } from './get-pricing-type-label';

export function buildRowsFromUsageProducts(
  usageProducts: UsageProduct[],
  catalogProductsById?: Map<string, CatalogProducts>,
): UsageProductsColumns[] {
  return usageProducts.flatMap((product) =>
    product.variants.flatMap((variant) => {
      const parentRow = createParentRow(product, variant, catalogProductsById);
      const tierRows = createTierRows(product, variant);

      return [parentRow, ...tierRows];
    }),
  );
}

function createParentRow(
  product: UsageProduct,
  variant: UsageProductVariant,
  catalogProductsById?: Map<string, CatalogProducts>,
): UsageProductsColumns {
  const { pricing } = variant;
  const isRevShare = isRevShareProductVariant(variant);
  const erp = getErpProduct(product.integrationEntities, product.externalIntegration);
  const erpMetadata = erp?.metadata as ERPIntegrationEntityMetadata | null | undefined;
  const erpId = erpMetadata?.displayId ?? erp?.integrationExternalId;
  const pricingType = getPricingTypeLabel(variant);

  const {
    anrokId: productAnrokId,
    anrokOverrideId: productAnrokOverrideId,
    anrokCatalogDefaultId: productAnrokCatalogDefaultId,
  } = resolveAnrokFields(product, catalogProductsById);

  return {
    id: `${product.id}-${variant.id}`,
    productId: product.id,
    product,
    productName: variant.name,
    price: getPrice(pricing),
    rate: product.basePrice,
    productErpId: erpId,
    productAnrokId,
    productAnrokOverrideId,
    productAnrokCatalogDefaultId,
    marketplaceProductId: product.cloudProviderSettings?.cloudProviderMarketplaceId,
    marketplaceDimension: product.cloudProviderSettings?.dimension,
    commitment: product.commitment?.amount,
    commitmentType: product.commitment?.type,
    bulk: getBulkUnits(pricing),
    isParent: isParentRow(variant),
    isExpanded: true,
    unitsType: variant.meter?.unitsType,
    meterReset: product.accumulationInterval,
    pricingType,
    meterRefill: product.commitment?.replenishmentInterval,
    billingInterval: product.billingInterval,
    isRevShare,
    startDate: withNewOptionalValue(product.startDate, formatDate),
    erp,
  };
}

function getBulkUnits(pricing: Pricing): number | undefined {
  return 'chunkSize' in pricing.priceConfiguration ? pricing.priceConfiguration.chunkSize : undefined;
}

function createTierRows(product: UsageProduct, variant: UsageProductVariant): UsageProductsColumns[] {
  const { pricing } = variant;

  if (isPerUnitPricing(pricing)) {
    return [];
  }

  const isRevShare = isRevShareProductVariant(variant);

  return pricing.priceConfiguration.tiers.map((tier) => ({
    id: `${product.id}-${variant.id}-${tier.from}`,
    product,
    productName: `Starts at ${formatNumber(tier.from)}`,
    price: tier.price,
    pricingType: '',
    rate: tier?.flatRate ?? 0,
    productErpId: '',
    productAnrokId: undefined,
    productAnrokOverrideId: undefined,
    productAnrokCatalogDefaultId: undefined,
    marketplaceProductId: undefined,
    marketplaceDimension: undefined,
    meterReset: undefined,
    meterRefill: undefined,
    bulk: tier.chunkSize,
    parentId: `${product.id}-${variant.id}`,
    isParent: false,
    hidden: false,
    isExpanded: false,
    isChild: true,
    key: `child-${product.id}-${variant.id}-${tier.from}`,
    unitsType: variant.meter?.unitsType,
    isRevShare,
  }));
}

function getPrice(pricing: Pricing) {
  if (isPerUnitPricing(pricing)) {
    return pricing.priceConfiguration.price;
  }

  if (isTieredSubscriptionPricing(pricing)) {
    return undefined;
  }

  return 0;
}

function isParentRow(variant: UsageProductVariant) {
  return !isPerUnitPricing(variant.pricing) && isNotEmptyArray(variant.pricing.priceConfiguration.tiers);
}
